import { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/AppContext';
import { useCrud } from '../../lib/hooks';
import { SttDialog } from '../../sporttia/dialogs/SttDialog';
import { SttFormCheck } from '../../sporttia/forms/SttFormCheck';

interface Consent {
    id: number;
    name: string;
    text: string;
    forBooking?: boolean;
}

interface ConsentsResponse {
    count: number;
    rows: Consent[];
}

interface BookingsConsentsPopUpProps {
    scId: number;
    onResponse: (accepted: boolean) => void;
}

export default function BookingsConsentsPopUp({
    scId,
    onResponse,
}: BookingsConsentsPopUpProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [consents, setConsents, , Get, , ,] = useCrud<Consent[]>();
    const [acceptConsents, setAcceptConsents] = useState(true);
    const [consentsResponses, setConsentsResponses] = useState<boolean[]>([]);

    /**
     * Load consents.
     * @param params Object with params like rows, page...
     */
    function loadConsents(
        params: Record<string, unknown> = { rows: 20 },
    ): void {
        Get(`/scs/${scId}/consents/templates`, {
            ...params,
            forBooking: true,
        }).then((response) => {
            const typedResponse = response as ConsentsResponse;
            if (typedResponse.count !== 0) {
                setConsents(typedResponse.rows);
                setAcceptConsents(false);
                setConsentsResponses(Array(typedResponse.count).fill(false));
            } else {
                setAcceptConsents(true);
                onResponse(true);
            }
        });
    }

    /**
     * Init
     */
    useEffect(() => {
        if (cxt?.logged && cxt?.user?.role !== 'SPORTCENTER') {
            loadConsents();
        } else {
            setAcceptConsents(true);
            onResponse(true);
        }
    }, []);

    /**
     * Change ConsentsResponses values.
     * @param index key value.
     * @param value value.
     */
    const updateConsentsResponses = (index: number, value: boolean): void => {
        const newArr = [...consentsResponses];
        newArr[index] = value;

        setConsentsResponses(newArr);
    };

    /**
     * Check if array values are equals.
     * @param arr Array.
     * @returns {boolean} True or False.
     */
    const allEqual = (arr: boolean[]): boolean =>
        arr.every((v: any) => v === arr[0] && v === true);

    return (
        <SttDialog
            open={!acceptConsents}
            title={t('Consents')}
            onClose={() => {
                onResponse(false);
                setAcceptConsents(false);
            }}
            content={
                Array.isArray(consents) &&
                consents.map((consent: any, key: any) => (
                    <Box key={consent.id}>
                        <SttFormCheck
                            name="forBooking"
                            checked={consentsResponses[key]}
                            onChange={() =>
                                updateConsentsResponses(
                                    key,
                                    !consentsResponses[key],
                                )
                            }
                        />
                        <b>{consent.name} </b>
                        <Typography
                            variant="body2"
                            align="justify"
                            gutterBottom
                        >
                            {consent.text}
                        </Typography>
                    </Box>
                ))
            }
            buttons={[
                {
                    type: 'accept',
                    disabled: !allEqual(consentsResponses),
                    caption: t('Accept'),
                    onClick: () => {
                        onResponse(true);
                        setAcceptConsents(false);
                    },
                },
                {
                    type: 'cancel',
                    onClick: () => {
                        onResponse(false);
                        setAcceptConsents(false);
                    },
                },
            ]}
        />
    );
}
